import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useAuthStore } from "../hooks/useAuthStore";
import {
  useNotificationsQuery,
  useMarkNotificationReadMutation,
  useMarkAllNotificationsReadMutation,
} from "../api/query/useNotificationQueries";
import {
  formatNotificationTime,
  getNotificationLink,
} from "../utils/notifications";
import NotificationAvatar from "./NotificationAvatar";

/**
 * Right-side notification drawer for the main site, mirroring the admin /
 * assistant notification panels. Includes an All / Unread tab toggle.
 *
 * Notifications are the signed-in user's own feed (GET /notifications, scoped
 * to the recipient server-side). The feed is only requested while the drawer is
 * open and a token exists, so guests on public pages never trigger a 401.
 */
function NotificationDrawer({ isOpen, onClose }) {
  const navigate = useNavigate();
  const { token, role } = useAuthStore();
  const [activeTab, setActiveTab] = useState("all");

  // Same params object as the assistant dashboard so both share the
  // ['notifications', {}] cache entry rather than double-fetching.
  const { data, isLoading, isError } = useNotificationsQuery(
    {},
    { enabled: Boolean(token) && isOpen }
  );

  const markAllReadMutation = useMarkAllNotificationsReadMutation();
  const markOneReadMutation = useMarkNotificationReadMutation();

  const notifications = useMemo(
    () =>
      (data?.data?.items || []).map((item) => ({
        id: item._id || item.id,
        // Carried through so the avatar can be resolved from the real person
        // the notification is about.
        actor: item.actor || null,
        title: item.title,
        message: item.body,
        time: formatNotificationTime(item.createdAt),
        unread: !item.isRead,
        link: getNotificationLink(item, role),
      })),
    [data, role]
  );

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const unreadCount = notifications.filter((n) => n.unread).length;

  const markAllAsRead = () => markAllReadMutation.mutate();

  const openNotification = (notification) => {
    if (notification.unread) markOneReadMutation.mutate(notification.id);
    if (notification.link) {
      onClose?.();
      navigate(notification.link);
    }
  };

  const visibleNotifications = useMemo(
    () =>
      activeTab === "unread"
        ? notifications.filter((n) => n.unread)
        : notifications,
    [activeTab, notifications]
  );

  return (
    <>
      <div
        className={`fixed inset-0 z-[1100] bg-[#0F172A]/50 backdrop-blur-[5px] transition-opacity duration-200 ${
          isOpen ? "opacity-100" : "pointer-events-none opacity-0"
        }`}
        onClick={onClose}
      />
      <aside
        className={`fixed inset-y-0 right-0 z-[1101] flex h-screen w-full max-w-[420px] flex-col overflow-hidden border-l border-[#EAECF0] bg-white shadow-[-20px_0_60px_rgba(16,24,40,0.18)] transition-transform duration-200 ${
          isOpen ? "translate-x-0" : "pointer-events-none translate-x-full"
        }`}
        aria-hidden={!isOpen}
      >
        <div className="flex items-center justify-between gap-4 border-b border-[#F2F4F7] px-5 py-5">
          <h2 className="text-[18px] font-semibold leading-none text-[#101828]">
            Notifications
          </h2>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={markAllAsRead}
              className="text-sm font-semibold text-[#61013B] disabled:cursor-not-allowed disabled:text-[#98A2B3]"
              disabled={unreadCount === 0}
            >
              Mark all as read
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md p-1.5 text-[#98A2B3] transition hover:bg-gray-50 hover:text-[#475467]"
              aria-label="Close notifications"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="px-5 py-4">
          <div className="inline-flex rounded-lg bg-[#F2F4F7] p-1">
            {[
              { id: "all", label: "All Notifications" },
              { id: "unread", label: "Unread" },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`rounded-md px-4 py-2 text-sm font-semibold transition ${
                  activeTab === tab.id
                    ? "bg-[#570033] text-white"
                    : "text-[#667085] hover:text-[#570033]"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          {visibleNotifications.length > 0 ? (
            visibleNotifications.map((notification, index) => (
              <div
                key={notification.id}
                onClick={() => openNotification(notification)}
                className={`flex cursor-pointer gap-3 px-5 py-4 transition ${
                  notification.unread
                    ? "bg-[#FAF7F9] hover:bg-[#F5EFF3]"
                    : "hover:bg-gray-50"
                } ${
                  index < visibleNotifications.length - 1
                    ? "border-b border-[#F2F4F7]"
                    : ""
                }`}
              >
                <div className="flex pt-2.5">
                  <span
                    className={`h-2.5 w-2.5 rounded-full ${
                      notification.unread ? "bg-[#7A0147]" : "bg-transparent"
                    }`}
                  />
                </div>

                <NotificationAvatar notification={notification} />

                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold leading-6 text-[#101828]">
                    {notification.title}
                  </p>
                  <p className="mt-1 text-sm font-medium leading-6 text-[#475467]">
                    {notification.message}
                  </p>
                  <p className="mt-2 text-xs font-medium text-[#98A2B3]">
                    {notification.time}
                  </p>
                </div>
              </div>
            ))
          ) : (
            <div className="flex h-full items-center justify-center px-6 py-10 text-center">
              <p className="text-sm font-medium text-[#667085]">
                {!token
                  ? "Log in to see your notifications."
                  : isLoading
                    ? "Loading your notifications…"
                    : isError
                      ? "We couldn't load your notifications. Please try again."
                      : activeTab === "unread"
                        ? "You're all caught up."
                        : "You have no notifications yet."}
              </p>
            </div>
          )}
        </div>
      </aside>
    </>
  );
}

export default NotificationDrawer;
